// Schemas Zod para validação de reuniões e relacionamentos

import { z } from 'zod';
import { MeetingStatusSchema, PriorityLevelSchema } from './event.schemas';

// Meeting Creation Schema (sem referências circulares)
export const MeetingCreateSchema = z.object({
  eventId: z.string().uuid('Event ID deve ser um UUID válido'),
  fabricanteId: z.string().uuid('Fabricante ID deve ser um UUID válido'),
  revendaId: z.string().uuid('Revenda ID deve ser um UUID válido').optional(),
  inicio: z.string().datetime('Data de início deve ser um datetime válido'),
  fim: z.string().datetime('Data de fim deve ser um datetime válido'),
  mesa: z.number().int().min(1, 'Mesa deve ser um número inteiro positivo').optional(),
  status: MeetingStatusSchema.default('Pendente'),
  resultadoNota: z.string().max(500, 'Nota de resultado deve ter no máximo 500 caracteres').optional(),
  observacoes: z.string().max(1000, 'Observações devem ter no máximo 1000 caracteres').optional()
});

// Meeting Update Schema
export const MeetingUpdateSchema = MeetingCreateSchema.partial().refine(
  (data) => Object.keys(data).length > 0,
  { message: 'Pelo menos um campo deve ser fornecido para atualização' }
);

// Relationship Creation Schema
export const RelationshipCreateSchema = z.object({
  fabricanteId: z.string().uuid('Fabricante ID deve ser um UUID válido'),
  revendaId: z.string().uuid('Revenda ID deve ser um UUID válido').optional(),
  prioridade: PriorityLevelSchema,
  eventId: z.string().uuid('Event ID deve ser um UUID válido'),
  observacoes: z.string().max(500, 'Observações devem ter no máximo 500 caracteres').optional()
});

// Relationship Update Schema  
export const RelationshipUpdateSchema = RelationshipCreateSchema.partial().refine(
  (data) => Object.keys(data).length > 0,
  { message: 'Pelo menos um campo deve ser fornecido para atualização' }
);

// Meeting Status Update Schema (operação comum)
export const MeetingStatusUpdateSchema = z.object({
  status: MeetingStatusSchema,
  resultadoNota: z.string().max(500).optional(),
  observacoes: z.string().max(1000).optional()
});

// Batch Meeting Update Schema
export const BatchMeetingUpdateSchema = z.object({
  meetingIds: z.array(z.string().uuid()).min(1, 'Pelo menos um meeting ID deve ser fornecido'),
  updates: MeetingUpdateSchema
});

// Meeting Query Filters Schema
export const MeetingQuerySchema = z.object({
  eventId: z.string().uuid().optional(),
  fabricanteId: z.string().uuid().optional(),
  revendaId: z.string().uuid().optional(),
  status: z.array(MeetingStatusSchema).optional(),
  dataInicio: z.string().datetime().optional(),
  dataFim: z.string().datetime().optional(),
  mesa: z.number().int().min(1).optional(),
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(100).default(10),
  sortBy: z.enum(['inicio', 'fim', 'status', 'mesa', 'createdAt']).default('inicio'),
  sortOrder: z.enum(['asc', 'desc']).default('asc')
});

// Export types
export type MeetingCreateType = z.infer<typeof MeetingCreateSchema>;
export type MeetingUpdateType = z.infer<typeof MeetingUpdateSchema>;
export type RelationshipCreateType = z.infer<typeof RelationshipCreateSchema>;
export type RelationshipUpdateType = z.infer<typeof RelationshipUpdateSchema>;
export type MeetingStatusUpdateType = z.infer<typeof MeetingStatusUpdateSchema>;
export type BatchMeetingUpdateType = z.infer<typeof BatchMeetingUpdateSchema>;
export type MeetingQueryType = z.infer<typeof MeetingQuerySchema>;